import { findImportTargetField } from "./target-fields.js";
import { importItemTypes, } from "./types.js";
// The proposal of a row: the mapped cells, trimmed and tidied, under their target field. It is a
// proposal (never canonical). It changes no meaning: no date, number, price, country or time zone is
// parsed here (a later phase validates and normalizes with rules and evidence). The raw cell stays
// in the row, unchanged.
/** The size the database allows for `normalized_proposal` (`octet_length(...::text)`). */
export const IMPORT_PROPOSAL_MAX_BYTES = 16_384;
/** Fields whose text may span lines. Every other field is one line. */
const MULTILINE_FIELDS = new Set(["description"]);
const INVISIBLE = /[​-‍⁠﻿]/g;
/**
 * The tidy text of a cell: Unicode NFC, no zero-width characters, no control characters, runs of
 * white space (a no-break space and a tab included) made one space, and the ends trimmed. A
 * multi-line field keeps its line breaks (at most one blank line in a row).
 */
export function normalizeCellText(cell, multiline = false) {
    const clean = cell
        .normalize("NFC")
        .replace(INVISIBLE, "")
        // biome-ignore lint/suspicious/noControlCharactersInRegex: the point is to remove them.
        .replace(/[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/g, "");
    if (!multiline)
        return clean.replace(/\s+/g, " ").trim();
    return clean
        .replace(/\r\n?/g, "\n")
        .split("\n")
        .map((line) => line.replace(/[^\S\n]+/g, " ").trim())
        .join("\n")
        .replace(/\n{3,}/g, "\n\n")
        .trim();
}
const itemTypeOf = (text) => {
    const upper = text.toUpperCase();
    return importItemTypes.includes(upper) ? upper : null;
};
// PostgreSQL prints jsonb with a space after each colon and comma, so the stored text is a little
// longer than `JSON.stringify`. The slack covers it (at most 36 fields and 200 list values).
const PRINT_SLACK_BYTES = 1024;
const jsonBytes = (value) => new TextEncoder().encode(JSON.stringify(value)).length;
/** Cuts the longest values until the proposal fits the column. The raw cell keeps the full text. */
function fit(fields) {
    const probe = () => jsonBytes({ itemType: null, fields });
    while (probe() > IMPORT_PROPOSAL_MAX_BYTES - PRINT_SLACK_BYTES) {
        let longest = null;
        for (const [key, value] of Object.entries(fields)) {
            const values = typeof value === "string" ? [value] : value;
            values.forEach((text, index) => {
                const length = [...text].length;
                if (longest === null || length > longest.length)
                    longest = { key, index, length };
            });
        }
        if (longest === null || longest.length <= 1)
            return;
        const pick = longest;
        const cut = (text) => [...text].slice(0, Math.max(1, Math.floor(pick.length / 2))).join("");
        const value = fields[pick.key];
        if (typeof value === "string")
            fields[pick.key] = cut(value);
        else if (value !== undefined)
            value[pick.index] = cut(value[pick.index] ?? "");
    }
}
/**
 * Builds the proposal of one row from its raw cells and the mapping. Only MAPPED columns count. A
 * field that ends up empty is left out. A field that takes several columns is a list in column order.
 * `itemType` is the mapped `item_type` text when it names a catalog type (any case), else the job
 * default, else `null`.
 */
export function buildProposal(input) {
    const fields = {};
    const mapped = input.decisions
        .filter((decision) => decision.decision === "MAPPED" && decision.targetField !== null)
        .sort((a, b) => a.columnIndex - b.columnIndex);
    for (const decision of mapped) {
        const targetField = decision.targetField;
        const target = findImportTargetField(targetField);
        if (target === undefined)
            continue;
        const text = normalizeCellText(input.rawCells[decision.columnIndex] ?? "", MULTILINE_FIELDS.has(targetField));
        if (text === "")
            continue;
        if (target.multiple) {
            const list = fields[targetField];
            if (Array.isArray(list))
                list.push(text);
            else
                fields[targetField] = [text];
        }
        else if (fields[targetField] === undefined) {
            fields[targetField] = text;
        }
    }
    fit(fields);
    const typeText = fields.item_type;
    const itemType = typeof typeText === "string" ? itemTypeOf(typeText) : input.defaultItemType;
    return Object.freeze({ itemType, fields });
}
